import { ArticleReference } from '../../redux/store/api/api';
import { VERSION_CONTENT_CONTAINER_ID } from '../../shared/constants';

export function getTextPassageIds(htmlString: string): string[] {
  const parser = new DOMParser();
  const htmlContent = parser.parseFromString(htmlString, 'text/html');

  const tagsWithId = htmlContent.querySelectorAll('[id]');

  const ids: string[] = [];
  tagsWithId.forEach((t) => {
    ids.push(t.getAttribute('id') || '');
  });

  return ids;
}

export function getTagsWithIds(htmlString: string): string[] {
  const parser = new DOMParser();
  const htmlContent = parser.parseFromString(htmlString, 'text/html');

  const tagsWithId = htmlContent.querySelectorAll('[id]');

  const tagsWithIds: string[] = [];

  tagsWithId.forEach((t) => {
    const divElement = document.createElement('div');
    divElement.classList.add('ps-2');
    divElement.append(t);
    const dummy = document.createElement('div');
    dummy.append(divElement);
    tagsWithIds.push(dummy.innerHTML);
  });

  return tagsWithIds;
}

export const createReferenceTitle = (
  abbreviation: string | null | undefined,
  name: string | null | undefined,
  originalName: string | null | undefined,
): string | undefined => {
  if (abbreviation === originalName) {
    return abbreviation ?? '';
  }
  if (abbreviation && originalName?.includes(abbreviation)) {
    return originalName;
  }
  return `${abbreviation && !name ? `${abbreviation} - ` : ''}${
    name || originalName || ''
  }`;
};

export const getLinkIsError = (reference: ArticleReference) => {
  const contentContainer = document.getElementById(
    VERSION_CONTENT_CONTAINER_ID,
  );

  return reference.sourceContentArea?.id
    ? contentContainer &&
        !contentContainer.querySelector(
          `[id*=${CSS.escape(reference.sourceContentArea.id)}]`,
        ) &&
        !reference.sourceContentArea.areaInvalid
    : true;
};

// sorting of links
const collator = new Intl.Collator('de', {
  sensitivity: 'base',
  numeric: true,
});

function romanToInt(roman: string): number | null {
  const map: Record<string, number> = {
    I: 1,
    V: 5,
    X: 10,
  };
  if (!roman) return null;
  let total = 0;
  for (let i = 0; i < roman.length; i += 1) {
    const v = map[roman[i]];
    const next = map[roman[i + 1]];
    if (next && v < next) total -= v;
    else total += v;
  }

  return total;
}

function parseAbbreviation(abbreviation?: string | null) {
  if (!abbreviation) return null;
  const m = abbreviation.trim().match(/^([A-ZÄÖÜß]+)\s+([IVX]+)\b/i);
  if (m) {
    const value = romanToInt(m[2]);
    return { base: m[1].toUpperCase(), roman: m[2].toUpperCase(), value };
  }

  return {
    base: abbreviation.trim().toUpperCase(),
    roman: null as string | null,
    value: null as number | null,
  };
}

function parseParagraph(
  originalName?: string | null,
): { n: number; s: string } | null {
  if (!originalName) return null;
  const paragraphRegex = /§{1,2}\s*(\d{1,4})\s*([a-zA-Z]{0,2})/;
  const match = originalName.match(paragraphRegex);
  if (!match) return null;

  return { n: Number(match[1]), s: (match[2] || '').toLowerCase() };
}

function compareParagraphs(
  a: { n: number; s: string } | null,
  b: { n: number; s: string } | null,
) {
  if (!a && b) return -1;
  if (a && !b) return 1;
  if (!a && !b) return 0;

  if (a!.n !== b!.n) return a!.n - b!.n;
  return collator.compare(a!.s, b!.s);
}

export function compareReferences(
  r1: ArticleReference,
  r2: ArticleReference,
  currentOpenedArticleId: string | null,
) {
  const abbreviation1 =
    r1.sourceArticleId === currentOpenedArticleId
      ? r1.categoryAbbreviation
      : r1.backwardCategoryAbbreviation;
  const abbreviation2 =
    r2.sourceArticleId === currentOpenedArticleId
      ? r2.categoryAbbreviation
      : r2.backwardCategoryAbbreviation;

  const parsedAbbr1 = parseAbbreviation(abbreviation1);
  const parsedAbbr2 = parseAbbreviation(abbreviation2);

  if (parsedAbbr1 && parsedAbbr2) {
    if (parsedAbbr1.base !== parsedAbbr2.base) {
      const baseComparison = collator.compare(
        parsedAbbr1.base,
        parsedAbbr2.base,
      );
      if (baseComparison !== 0) return baseComparison;
    }

    if (parsedAbbr1.value !== null || parsedAbbr2.value !== null) {
      if (parsedAbbr1.value === null) return 1;
      if (parsedAbbr2.value === null) return -1;
      if (parsedAbbr1.value !== parsedAbbr2.value)
        return parsedAbbr1.value - parsedAbbr2.value;
    }
  } else if (parsedAbbr1 && !parsedAbbr2) {
    return -1;
  } else if (!parsedAbbr1 && parsedAbbr2) {
    return 1;
  }

  const original1 =
    r1.sourceArticleId === currentOpenedArticleId
      ? r1.originalName
      : r1.originalBackwardName;
  const original2 =
    r2.sourceArticleId === currentOpenedArticleId
      ? r2.originalName
      : r2.originalBackwardName;

  const parsedPar1 = parseParagraph(original1);
  const parsedPar2 = parseParagraph(original2);
  const baseComparison = compareParagraphs(parsedPar1, parsedPar2);
  if (baseComparison !== 0) return baseComparison;

  const title1 =
    r1.sourceArticleId === currentOpenedArticleId
      ? createReferenceTitle(
          r1.categoryAbbreviation,
          r1.name,
          r1.originalName,
        ) || ''
      : createReferenceTitle(
          r1.backwardCategoryAbbreviation,
          r1.backwardName,
          r1.originalBackwardName,
        ) || '';

  const title2 =
    r2.sourceArticleId === currentOpenedArticleId
      ? createReferenceTitle(
          r2.categoryAbbreviation,
          r2.name,
          r2.originalName,
        ) || ''
      : createReferenceTitle(
          r2.backwardCategoryAbbreviation,
          r2.backwardName,
          r2.originalBackwardName,
        ) || '';

  return collator.compare(title1, title2);
}
